import { createContext, useContext, useState, ReactNode } from 'react'
import { resetProducts } from '@/config/products'
import { authService, User, CreateOwnerRequest, LoginResponse, TOTPRequiredResponse } from '@/services/authService'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { clearLastActivity } from '@/lib/sessionTimeout'

interface AuthContextType {
  user: User | null
  login: (account: string, password: string) => Promise<LoginResponse | TOTPRequiredResponse>
  verifyTOTPLogin: (tempToken: string, totpCode: string) => Promise<void>
  verifyRecoveryCodeLogin: (tempToken: string, recoveryCode: string) => Promise<void>
  logout: () => Promise<void>
  createOwnerAndLogin: (data: CreateOwnerRequest) => Promise<void>
  refreshAuth: () => Promise<void>
  updateUser: (user: User) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize user from localStorage immediately
  const [user, setUser] = useState<User | null>(() => authService.getUser())

  // Ends the session after an hour idle, or when the token expires.
  useSessionTimeout(!!user)

  const login = async (account: string, password: string) => {
    const response = await authService.login({ account, password })

    // If TOTP is not required, set user immediately
    if ('user' in response) {
      setUser(response.user)
    }

    return response
  }

  const verifyTOTPLogin = async (tempToken: string, totpCode: string) => {
    const response = await authService.verifyTOTPLogin({
      temp_token: tempToken,
      totp_code: totpCode,
    })
    setUser(response.user)
  }

  const verifyRecoveryCodeLogin = async (tempToken: string, recoveryCode: string) => {
    const response = await authService.verifyRecoveryCodeLogin({
      temp_token: tempToken,
      recovery_code: recoveryCode,
    })
    setUser(response.user)
  }

  const logout = async () => {
    resetProducts()
    await authService.logout()
    clearLastActivity()
    setUser(null)
  }

  const createOwnerAndLogin = async (data: CreateOwnerRequest) => {
    const response = await authService.createOwnerAndLogin(data)
    // Only set user if not TOTP required (new owners won't have TOTP)
    if ('user' in response) {
      setUser(response.user)
    }
  }

  const refreshAuth = async () => {
    const refreshedUser = await authService.refreshToken()
    setUser(refreshedUser)
  }

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser)
  }

  return (
    <AuthContext.Provider value={{ user, login, verifyTOTPLogin, verifyRecoveryCodeLogin, logout, createOwnerAndLogin, refreshAuth, updateUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
